import { useIntl } from "react-intl";
import { Box, Typography } from "@mui/material";

import { InfoButton } from "../../components/Tooltips";
import { VARIABLE_LABELS } from "./constants";
import { MLMetrics } from "./services";

const TrainingTiles = ({ model }: { model: MLMetrics }) => {
  const intl = useIntl();

  const total = model.n_train + model.n_test;
  const trainPct = total > 0 ? ((model.n_train / total) * 100).toFixed(0) : "—";
  const testPct = total > 0 ? ((model.n_test / total) * 100).toFixed(0) : "—";
  const nFeatures = model.features?.length ?? 0;

  const trainedAt = model.trained_at
    ? intl.formatDate(model.trained_at, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  const featureList = model.features?.length
    ? model.features.map((f) => VARIABLE_LABELS[f] ?? f).join(", ")
    : "—";

  const tiles: {
    label: string;
    value: string;
    sub?: string;
    tooltip: string;
  }[] = [
    {
      label: "Entraînement",
      value: `${intl.formatNumber(model.n_train)} résa`,
      sub: `${trainPct}% des données`,
      tooltip: `Les réservations des 2 dernières années sont triées par date de début de séjour. Les ${trainPct}% les plus anciennes servent à entraîner le modèle.`,
    },
    {
      label: "Test",
      value: `${intl.formatNumber(model.n_test)} résa`,
      sub: `${testPct}% des données`,
      tooltip: `Les ${testPct}% de réservations les plus récentes forment le jeu de test. Le modèle ne les a jamais vues pendant l'apprentissage — elles servent uniquement à évaluer ses performances réelles.`,
    },
    {
      label: "Variables utilisées",
      value: `${nFeatures} features`,
      sub: undefined,
      tooltip:
        model.model_name === "prob_vente_custom"
          ? `Variables choisies manuellement pour ce modèle personnalisé :\n${featureList}`
          : `Features retenues par les tests statistiques (KS pour les variables quantitatives, Khi² pour les qualitatives) :\n${featureList}`,
    },
    ...(trainedAt
      ? [
          {
            label: "Dernier entraînement",
            value: trainedAt,
            sub: undefined,
            tooltip:
              "Date à laquelle le modèle a été entraîné et sauvegardé sur S3. Un nouvel entraînement remplace le modèle précédent.",
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        mt: 1.5,
        justifyContent: "center",
      }}
    >
      {tiles.map(({ label, value, sub, tooltip }) => (
        <Box
          key={label}
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            flex: 1,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {label}
            </Typography>
            <InfoButton title={tooltip} translateTitle={false} />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
            }}
          >
            {value}
          </Typography>
          {sub && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {sub}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default TrainingTiles;
